import type { SelectOption } from '@scaleaq/scaleui';
import type { WebRtcStream } from '@scaleaq/sitecontrol-lib';

import { useTranslation } from 'react-i18next';

import { CameraStreamControlButton } from '@/components/cameraStreamWidget/CameraStreamControlButton.tsx';
import {
  StyledProfileGlyph,
  StyledStreamProfileToggleHost,
} from '@/components/cameraStreamWidget/cameraStreamSideControls/cameraStreamProfileToggleButton/cameraStreamProfileToggleButton.styles.ts';
import { Loader } from '@/components/Loader.tsx';

import { useCameraStreamFrameControlsContext } from '../cameraStreamWidgetWrapper/cameraStreamFrameControlsProvider/CameraStreamFrameControlsContext.ts';

type CameraStreamProfileToggleButtonProps = {
  currentStream: SelectOption<WebRtcStream>;
  streams: SelectOption<WebRtcStream>[];
  loading: boolean;
  disabled: boolean;
  onToggle: (stream: SelectOption<WebRtcStream>) => void;
};

export const CameraStreamProfileToggleButton = ({
  currentStream,
  streams,
  loading,
  disabled,
  onToggle,
}: CameraStreamProfileToggleButtonProps) => {
  const { frameControlsVisible } = useCameraStreamFrameControlsContext();
  const { t } = useTranslation('cameraControl');
  const currentIndex = streams.findIndex(stream => stream.id === currentStream.id);
  const nextStream = streams[(currentIndex + 1) % streams.length];
  const tooltip = loading
    ? t('switchingStreamProfile')
    : t('switchStreamProfile', {
        newProfile: nextStream.label,
        profile: currentStream.label,
      });

  return (
    <StyledStreamProfileToggleHost
      $visible={frameControlsVisible || loading}
      onMouseDown={event => {
        // Keep focus on the card. Native `disabled` during the switch would drop it
        // from this button and hide the widget selection border.
        event.preventDefault();
      }}
    >
      <Loader variant='bubbles' size='small' render={loading} showWaitCursor />

      <CameraStreamControlButton
        disabled={disabled || loading}
        icon={
          <StyledProfileGlyph>
            {getShortProfileLabel(currentStream, Math.max(currentIndex, 0))}
          </StyledProfileGlyph>
        }
        label={tooltip}
        onClick={() => onToggle(nextStream)}
      />
    </StyledStreamProfileToggleHost>
  );
};

/**
 * Cameras that expose extra profiles beyond a high/low pair fall back to the profile number,
 * since an arbitrary ONVIF profile name has no meaningful two-letter tag.
 */
const getShortProfileLabel = (stream: SelectOption<WebRtcStream>, streamIndex: number): string => {
  const profileName = `${String(stream.id ?? '')} ${stream.label}`.toUpperCase();
  if (/(^|[^A-Z])(HIGH|HQ)([^A-Z]|$)/.test(profileName)) return 'HQ';
  if (/(^|[^A-Z])(LOW|LQ|LO)([^A-Z]|$)/.test(profileName)) return 'LO';
  return String(streamIndex + 1);
};
